import { createContext, useContext } from 'react';

/**
 * Tenant connection metadata, shared across dashboard views so rows can build
 * deep links to Stripe + HubSpot (e.g. "View in Stripe" / "View in HubSpot").
 *
 * Populated once by DashboardLayout (via GET /api/admin/overview) and provided
 * through ConnectionContext. Views consume it via useConnection(). Both fields
 * may be null when the tenant isn't fully connected or the overview fetch
 * hasn't resolved yet — link builders fall back gracefully (omit the link).
 */
export interface ConnectionInfo {
  /** HubSpot portal id (accountId) — used to build app.hubspot.com URLs. */
  portalId: string | null;
  /** Stripe account id (acct_…) — used to build dashboard.stripe.com URLs. */
  stripeAccountId: string | null;
  /** M31: true when the Stripe app is connected in test mode (dashboard banner). */
  testMode?: boolean;
}

export const ConnectionContext = createContext<ConnectionInfo>({
  portalId: null,
  stripeAccountId: null,
});

/** Read the tenant connection info (portalId + stripeAccountId). */
export function useConnection(): ConnectionInfo {
  return useContext(ConnectionContext);
}

// ─── URL builders ──────────────────────────────────────────────────────────
//
// Stripe dashboard URLs: for connected accounts the canonical form is
//   https://dashboard.stripe.com/{acct}/<resource>/{id}
// The account prefix scopes the view to the connected account; without it the
// dashboard may route to the platform account (which has no such customer/
// subscription). When stripeAccountId is null we omit the link (can't reliably
// deep-link without knowing the account).
//
// HubSpot URLs:
//   https://app.hubspot.com/contacts/{portalId}/<object>/{id}
// These are stable, well-documented HubSpot CRM deep links.

/** Stripe customer dashboard URL, or null when stripeAccountId is unknown. */
export function stripeCustomerUrl(
  stripeAccountId: string | null,
  customerId: string
): string | null {
  if (!stripeAccountId) return null;
  return `https://dashboard.stripe.com/${stripeAccountId}/customers/${customerId}`;
}

/** Stripe subscription dashboard URL, or null when stripeAccountId is unknown. */
export function stripeSubscriptionUrl(
  stripeAccountId: string | null,
  subscriptionId: string
): string | null {
  if (!stripeAccountId) return null;
  return `https://dashboard.stripe.com/${stripeAccountId}/subscriptions/${subscriptionId}`;
}

/** Stripe product dashboard URL, or null when stripeAccountId is unknown. */
export function stripeProductUrl(
  stripeAccountId: string | null,
  productId: string
): string | null {
  if (!stripeAccountId) return null;
  return `https://dashboard.stripe.com/${stripeAccountId}/products/${productId}`;
}

/** Stripe price dashboard URL, or null when stripeAccountId is unknown. */
export function stripePriceUrl(
  stripeAccountId: string | null,
  priceId: string
): string | null {
  if (!stripeAccountId) return null;
  return `https://dashboard.stripe.com/${stripeAccountId}/prices/${priceId}`;
}

/** HubSpot Contact record URL, or null when portalId is unknown. */
export function hubspotContactUrl(
  portalId: string | null,
  contactId: string
): string | null {
  if (!portalId || !contactId) return null;
  return `https://app.hubspot.com/contacts/${portalId}/contact/${contactId}`;
}

/** HubSpot Deal record URL, or null when portalId is unknown. */
export function hubspotDealUrl(
  portalId: string | null,
  dealId: string
): string | null {
  if (!portalId || !dealId) return null;
  return `https://app.hubspot.com/contacts/${portalId}/deal/${dealId}`;
}

/** HubSpot Product record URL, or null when portalId is unknown. */
export function hubspotProductUrl(
  portalId: string | null,
  productId: string
): string | null {
  if (!portalId || !productId) return null;
  return `https://app.hubspot.com/contacts/${portalId}/product/${productId}`;
}

// ─── M19 T2: Stripe transactional dashboard URLs ───────────────────────────
//
// Charges and refunds are both surfaced under the Stripe "payments" surface
// (a charge's dashboard URL is /payments/{ch_id}; a refund has no standalone
// page, so its "view in Stripe" links to the parent charge's payment page).
// Invoices and disputes have their own dedicated surfaces.

/** Stripe Charge payment URL, or null when stripeAccountId is unknown. */
export function stripeChargeUrl(
  stripeAccountId: string | null,
  chargeId: string
): string | null {
  if (!stripeAccountId) return null;
  return `https://dashboard.stripe.com/${stripeAccountId}/payments/${chargeId}`;
}

/** Stripe Invoice URL, or null when stripeAccountId is unknown. */
export function stripeInvoiceUrl(
  stripeAccountId: string | null,
  invoiceId: string
): string | null {
  if (!stripeAccountId) return null;
  return `https://dashboard.stripe.com/${stripeAccountId}/invoices/${invoiceId}`;
}

/** Stripe Dispute URL, or null when stripeAccountId is unknown. */
export function stripeDisputeUrl(
  stripeAccountId: string | null,
  disputeId: string
): string | null {
  if (!stripeAccountId) return null;
  return `https://dashboard.stripe.com/${stripeAccountId}/disputes/${disputeId}`;
}

/**
 * Stripe Refund URL. Refunds have no standalone dashboard page; this links to
 * the parent charge's payment page (where the refund is listed). Null when
 * stripeAccountId is unknown.
 */
export function stripeRefundUrl(
  stripeAccountId: string | null,
  chargeId: string
): string | null {
  if (!stripeAccountId) return null;
  return `https://dashboard.stripe.com/${stripeAccountId}/payments/${chargeId}`;
}
